// Prompt 381 — AdminPersonaPanel: overlay modal que replica PersonaDetailPage
// como componente embebido en la pantalla de administración (sin navegación).
// Se invoca desde AdminPersonas cuando el CEO clica una fila.

import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '../../../../../i18n';
import { useCurrentUser } from '../../../../../hooks/useCurrentUser';
import { ConfirmDialog } from '../../../../shared/components/ConfirmDialog';
import { AdminOverlayPanel } from './shared/AdminOverlayPanel';
import {
  fetchPersona,
  actualizarPersona,
  darDeBajaPersona,
  request,
  type PersonaDetalle,
} from '../../../../shared/services/api';
import { tabStyle, type TabKey } from '../../people/components/persona-detail-shared';
import { PersonaDatosTab } from '../../people/components/PersonaDatosTab';
import { PersonaPuestoTab } from '../../people/components/PersonaPuestoTab';
import { PersonaProyectosTab } from '../../people/components/PersonaProyectosTab';
import { PersonaCentrosTab } from '../../people/components/PersonaCentrosTab';
import { PersonaActivosTab } from '../../people/components/PersonaActivosTab';
import { PersonaProcesosDocsTab } from '../../people/components/PersonaProcesosDocsTab';
import { PersonaGobernanzaTab } from '../../people/components/PersonaGobernanzaTab';
import { C } from '../../../../../lib/design';

interface AdminPersonaPanelProps {
  personaId: string | null;
  onClose: () => void;
  onRefresh: () => void;
}

export function AdminPersonaPanel({ personaId, onClose, onRefresh }: AdminPersonaPanelProps) {
  const { t } = useI18n();
  const { isAdmin } = useCurrentUser();
  // 0610: navegar a Configuracion -> Usuarios con deep-link al modal
  // Gestionar (?personaId=X) o Invitar (?invitarPersonaId=X).
  const navigate = useNavigate();
  const adm = t.structure.admin;
  const panel = adm.personaPanel;

  const TABS: { key: TabKey; label: string }[] = [
    { key: 'datos', label: t.people.personalData },
    { key: 'puesto', label: t.people.positionAndChart },
    { key: 'procesos', label: t.people.processesAndDocs ?? 'Procesos y Documentos' },
    { key: 'proyectos', label: t.people.projects },
    { key: 'centros', label: t.people.centers },
    { key: 'activos', label: t.people.assets },
    { key: 'gobernanza', label: t.people.governanceTab },
  ];

  // Escape gestionado por AdminOverlayPanel (desde 0483).

  const [data, setData] = useState<PersonaDetalle | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<TabKey>('datos');
  const [error, setError] = useState('');
  const [confirmState, setConfirmState] = useState<{
    open: boolean;
    title: string;
    message: string;
    danger?: boolean;
    onConfirm: () => void;
  } | null>(null);

  const [usuarioVinculado, setUsuarioVinculado] = useState<{
    id: string;
    email: string;
    nombreCompleto: string;
    rol: string;
    activo: boolean;
  } | null>(null);

  const loadData = useCallback(async () => {
    if (!personaId) return;
    setLoading(true);
    setError('');
    try {
      const res = await fetchPersona(personaId);
      setData(res.data);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Error');
    } finally {
      setLoading(false);
    }
  }, [personaId]);

  useEffect(() => {
    if (!personaId) {
      setData(null);
      setUsuarioVinculado(null);
      setActiveTab('datos');
      return;
    }
    loadData();
    request<{
      data: Array<{
        id: string;
        email: string;
        nombreCompleto: string;
        rol: string;
        activo: boolean;
      }>;
      meta: unknown;
    }>(`/admin/users?personaId=${personaId}&limit=1`)
      .then((r) => setUsuarioVinculado(r.data?.[0] ?? null))
      .catch(() => setUsuarioVinculado(null));
  }, [personaId, loadData]);

  if (!personaId) return null;

  const handleSave = async (updates: Record<string, unknown>) => {
    await actualizarPersona(personaId, updates);
    await loadData();
    onRefresh();
  };

  const handleBaja = () => {
    setConfirmState({
      open: true,
      title: t.people.terminate,
      message: t.people.confirmTerminate,
      danger: true,
      onConfirm: async () => {
        await darDeBajaPersona(personaId);
        setConfirmState(null);
        onRefresh();
        onClose();
      },
    });
  };

  const p = data?.persona ?? null;

  return (
    <AdminOverlayPanel open={personaId !== null} onClose={onClose}>
      <>
        {/* Cierre por boton X (AdminOverlayPanel) o tecla Escape. Overlay no bloquea click-to-switch. */}

        {/* Cabecera fija (breadcrumb + nombre + baja + tabs) — solo con data */}
        {!loading && data && p && (
          <div
            style={{
              flexShrink: 0,
              padding: '24px 32px 0',
              borderBottom: '1px solid #E2E6ED',
              background: '#fff',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 12,
                marginBottom: 12,
              }}
            >
              <h2
                style={{
                  margin: 0,
                  fontSize: 20,
                  fontWeight: 700,
                  color: C.azul1,
                }}
              >
                {p.nombre} {p.apellidos}
              </h2>
              {p.estado === 'ACTIVO' && (
                <button
                  onClick={handleBaja}
                  style={{
                    padding: '6px 14px',
                    border: '1px solid #DC2626',
                    borderRadius: 6,
                    background: 'transparent',
                    color: '#DC2626',
                    cursor: 'pointer',
                    fontSize: 12,
                    fontFamily: 'inherit',
                  }}
                >
                  {t.people.terminate}
                </button>
              )}
            </div>

            <div
              style={{
                display: 'flex',
                background: '#fff',
              }}
            >
              {TABS.map((tab) => (
                <button
                  key={tab.key}
                  style={tabStyle(activeTab === tab.key)}
                  onClick={() => setActiveTab(tab.key)}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Zona de contenido scrollable (flex:1 overflow:auto) */}
        <div style={{ flex: 1, overflow: 'auto', padding: '16px 32px 24px' }}>
          {loading && (
            <div style={{ textAlign: 'center', color: '#9CA3AF', padding: 80 }}>
              {t.people.loadingPerson}
            </div>
          )}

          {!loading && (error || !data || !p) && (
            <div style={{ textAlign: 'center', color: '#DC2626', padding: 80 }}>
              {error || t.people.personNotFound}
            </div>
          )}

          {!loading && data && p && (
            <>
              <div style={{ paddingBottom: 24 }}>
                {activeTab === 'datos' && <PersonaDatosTab data={data} onSave={handleSave} />}
                {activeTab === 'puesto' && (
                  <PersonaPuestoTab data={data} personaId={personaId} onRefresh={loadData} />
                )}
                {activeTab === 'procesos' && <PersonaProcesosDocsTab personaId={personaId} />}
                {activeTab === 'proyectos' && <PersonaProyectosTab personaId={personaId} />}
                {activeTab === 'centros' && <PersonaCentrosTab personaId={personaId} />}
                {activeTab === 'activos' && <PersonaActivosTab data={data} />}
                {activeTab === 'gobernanza' && (
                  <PersonaGobernanzaTab data={data} personaId={personaId} onRefresh={loadData} />
                )}
              </div>

              {/* Usuario vinculado */}
              {isAdmin && (
                <div
                  style={{
                    marginTop: 16,
                    padding: '16px 20px',
                    background: '#F8FAFC',
                    borderRadius: 10,
                    border: '1px solid #E5E7EB',
                  }}
                >
                  <div
                    style={{
                      fontSize: 13,
                      fontWeight: 700,
                      color: '#0E243E',
                      marginBottom: 10,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                    }}
                  >
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                    >
                      <rect x="3" y="11" width="18" height="11" rx="2" />
                      <path d="M7 11V7a5 5 0 0110 0v4" />
                    </svg>
                    {usuarioVinculado ? adm.usuarioVinculado : adm.sinUsuarioVinculado}
                  </div>
                  {usuarioVinculado ? (
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: 12,
                      }}
                    >
                      <div style={{ fontSize: 13, color: '#374151' }}>
                        <div>
                          <strong>{panel.usuario.email}:</strong> {usuarioVinculado.email}
                        </div>
                        <div>
                          <strong>{panel.usuario.rol}:</strong> {usuarioVinculado.rol}
                        </div>
                        <div>
                          <strong>{panel.usuario.estado}:</strong>{' '}
                          {usuarioVinculado.activo
                            ? t.common.estados.activo
                            : t.common.estados.inactivo}
                        </div>
                      </div>
                      <button
                        onClick={() => {
                          // 0610: deep-link al modal Gestionar de Configuracion -> Usuarios
                          navigate(`/admin?tab=usuarios&personaId=${personaId}`);
                          onClose();
                        }}
                        style={{
                          padding: '6px 14px',
                          background: C.azul2,
                          color: '#fff',
                          border: 'none',
                          borderRadius: 6,
                          fontSize: 12,
                          fontWeight: 600,
                          cursor: 'pointer',
                          fontFamily: 'inherit',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {adm.gestionarAcceso} →
                      </button>
                    </div>
                  ) : (
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: 12,
                      }}
                    >
                      <span style={{ fontSize: 13, color: '#6B7280' }}>{panel.sinUsuario}</span>
                      <button
                        onClick={() => {
                          // 0610: abrir modal Invitar de Configuracion -> Usuarios
                          // pre-seleccionando esta persona en modo "Vincular existente"
                          navigate(`/admin?tab=usuarios&invitarPersonaId=${personaId}`);
                          onClose();
                        }}
                        style={{
                          padding: '6px 14px',
                          background: C.azul2,
                          color: '#fff',
                          border: 'none',
                          borderRadius: 6,
                          fontSize: 12,
                          fontWeight: 600,
                          cursor: 'pointer',
                          fontFamily: 'inherit',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {adm.linkUser} →
                      </button>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {confirmState && (
          <ConfirmDialog
            open={confirmState.open}
            title={confirmState.title}
            message={confirmState.message}
            danger={confirmState.danger}
            onConfirm={confirmState.onConfirm}
            onCancel={() => setConfirmState(null)}
          />
        )}
      </>
    </AdminOverlayPanel>
  );
}
